import { getAgentHold, holdAgent, resumeAgent } from '../team-hold/teamHold';
import type {
  InvCallGroup,
  InvEmail,
  InvLatestItem,
  InvLineConversation,
  InvLinePassed,
  InvLineQuestion,
  InvLineUnanswered,
  InvLineReply,
  InvLogOption,
  InvNumbers,
  InvPatient,
  InvProgrammeMessage,
  InvProspect,
  InvSettingsSignal,
  InvStage,
} from './types';

/* ---- §1 the stage vocabulary (same word + colour everywhere) ------------- */

export const INV_STAGE_META: Record<InvStage, { label: string; fg: string; bg: string }> = {
  'worth-asking': { label: 'Worth asking', fg: 'var(--text-muted)', bg: 'var(--chip-fill)' },
  learning: { label: 'Learning', fg: 'var(--brand-indigo)', bg: 'var(--brand-indigo-tint)' },
  ready: { label: 'Ready', fg: 'var(--warn-amber)', bg: 'var(--warn-amber-fill)' },
  'consult-booked': { label: 'Consult booked', fg: 'var(--money-green)', bg: 'var(--money-green-tint)' },
  accepted: { label: 'Accepted', fg: 'var(--money-green)', bg: 'var(--money-green-tint)' },
  'asked-not-now': { label: 'Asked — not now', fg: 'var(--text-muted)', bg: 'var(--chip-fill)' },
  closed: { label: 'Closed', fg: 'var(--text-muted)', bg: 'var(--chip-fill)' },
};

/* ---- §1 the numbers ------------------------------------------------------ */

export const INV_NUMBERS: InvNumbers = {
  worthAsking: 23,
  learning: 14,
  ready: 7,
  consultBooked: 5,
  askedNotNow: 14,
  all: 63,
  readyToCall: 5,
  readyComingIn: 2,
  asked90: 38,
  saidYes: 24,
  saidYesNotNow: 11,
  saidYesNo: 3,
  became90Ready: 15,
  consults90: 8,
  started90: 2,
  casesValue: '$11,600',
  bookedAfterCall: 4,
  bookedAfterLink: 3,
  bookedAfterLine: 1,
  comingIn14: 9,
  unusedBenefit: '$31,500',
  unusedBenefitPatients: 19,
  lineConversations: 41,
  lineTexts: 29,
  lineCalls: 12,
  linePatients: 26,
  lineBecameReady: 7,
  lineBooked: 2,
  lineUnanswered: 3,
  providerLevel: 'Gold',
  isProvider: true,
  consultType: 'Aligner consult · 45 min · Dr. Carter',
  lineNumber: '(210) 555-0199',
};

/* ---- hold (§3, spec 24 §6) ----------------------------------------------- */

export const invHoldStore = { get onHold(): boolean { return getAgentHold('invisalign').held; } };
export function setInvHold(onHold: boolean): void {
  if (onHold) holdAgent('invisalign', { by: 'Ava Thompson', since: '9:10 am', waiting: 2 });
  else resumeAgent('invisalign');
}

/* ---- §3.1 the ten emails (§7 canonical set; Settings matches these) ------ */

export const INV_EMAILS: InvEmail[] = [
  { day: 0, subject: 'What clear aligners actually are', sent: 24, opened: 19 },
  { day: 5, subject: 'Why people with {Smile profile} use them', sent: 23, opened: 16 },
  { day: 11, subject: 'What it costs, and what your plan covers', sent: 22, opened: 17 },
  { day: 17, subject: 'Aligners or braces — an honest comparison', sent: 20, opened: 12 },
  { day: 24, subject: 'How long people usually wear them', sent: 18, opened: 11 },
  { day: 31, subject: 'What the first appointment involves', sent: 15, opened: 10 },
  { day: 38, subject: 'Eating, cleaning, and the bits people ask about', sent: 12, opened: 8 },
  { day: 45, subject: 'A question we get a lot', sent: 9, opened: 7 },
  { day: 52, subject: 'What happens when you finish', sent: 6, opened: 3 },
  { day: 58, subject: 'If you ever want to talk it through', sent: 3, opened: 2 },
];

/* ---- §3.2 prospects ------------------------------------------------------ */

export const INV_PROSPECTS: InvProspect[] = [
  { id: 'nadia-karimi', name: 'Nadia Karimi', age: 38, since: '2019', signals: [{ label: 'Chart note', date: '12 Sep' }, { label: 'Unused ortho benefit' }], orthoBenefit: '$2,000', lastVisit: '12 Sep', nextAppt: '3 Oct · 9:00 am', stage: 'worth-asking' },
  { id: 'tomas-reyes', name: 'Tomás Reyes', age: 29, since: '2021', signals: [{ label: 'Unused ortho benefit' }], orthoBenefit: '$2,000', lastVisit: '30 Aug', nextAppt: '1 Oct · 2:30 pm', stage: 'worth-asking' },
  { id: 'chloe-nguyen', name: 'Chloe Nguyen', age: 24, since: '2015', signals: [{ label: 'Family member in treatment' }, { label: 'Unused ortho benefit' }], orthoBenefit: '$1,000', lastVisit: '2 Jul', nextAppt: '14 Oct · 11:00 am', stage: 'worth-asking' },
  { id: 'ravi-shah', name: 'Ravi Shah', age: 41, since: '2012', signals: [{ label: 'Scan in 2024, didn’t start' }], orthoBenefit: '$1,500', lastVisit: '18 Aug', nextAppt: '21 Oct · 8:00 am', stage: 'worth-asking' },
  { id: 'jacob-stein', name: 'Jacob Stein', age: 33, since: '2020', signals: [{ label: 'Unused ortho benefit' }], orthoBenefit: '$1,000', lastVisit: '5 Sep', nextAppt: '7 Oct · 4:00 pm', stage: 'worth-asking' },
  { id: 'ben-hollis', name: 'Ben Hollis', age: 45, since: '2017', signals: [{ label: 'Asked ELVA about aligners', date: 'text 20 Sep' }, { label: 'Unused ortho benefit' }], orthoBenefit: '$2,000', lastVisit: '9 Sep', nextAppt: '9 Oct · 10:30 am', stage: 'worth-asking' },
  { id: 'maria-costa', name: 'Maria Costa', age: 27, since: '2022', signals: [{ label: 'Interest noted in the chart', date: '3 Sep' }, { label: 'Unused ortho benefit' }], orthoBenefit: '$1,500', lastVisit: '3 Sep', nextAppt: '11 Oct · 1:00 pm', stage: 'worth-asking' },
  { id: 'devon-price', name: 'Devon Price', age: 36, since: '2016', signals: [{ label: 'Chart note', date: '28 Aug' }, { label: 'Unused ortho benefit' }], orthoBenefit: '$2,000', lastVisit: '28 Aug', nextAppt: '13 Oct · 9:30 am', stage: 'worth-asking' },

  // Learning — said yes, getting the programme (stageDetail = which day they're on).
  { id: 'nadia-karimi-l', name: 'Nadia Karimi', age: 38, since: '2019', signals: [{ label: 'Said yes at the chair', date: '30 Aug' }], orthoBenefit: '$2,000', lastVisit: '30 Aug', nextAppt: '—', stage: 'learning', stageDetail: 'Day 31 of 58' },
  { id: 'grace-whitfield', name: 'Grace Whitfield', age: 34, since: '2018', signals: [{ label: 'Replied to the day-11 email', date: '26 Sep' }], orthoBenefit: '$1,500', lastVisit: '14 Aug', nextAppt: '—', stage: 'learning', stageDetail: 'Day 12 of 58' },
  { id: 'marcus-reid', name: 'Marcus Reid', age: 31, since: '2020', signals: [{ label: 'Opened 5 emails, no reply' }], orthoBenefit: '$1,000', lastVisit: '24 Sep', nextAppt: '—', stage: 'learning', stageDetail: 'Day 24 of 58' },

  // Ready — a Ready signal fired; on the Call list, or coming in soon.
  { id: 'p001', name: 'Orville Allison', age: 34, since: '2018', signals: [{ label: 'Clicked the consult link', date: '27 Sep' }, { label: 'Asked the Invis-Line about cost', date: '24 Sep' }], orthoBenefit: '$2,000', lastVisit: '3 Aug', nextAppt: 'Today · 5:00 pm', stage: 'ready', stageDetail: 'On the Call list' },
  { id: 'hannah-cole', name: 'Hannah Cole', age: 26, since: '2019', signals: [{ label: 'Asked the Invis-Line about timing', date: '25 Sep' }], orthoBenefit: '$1,500', lastVisit: '25 Sep', nextAppt: '3 Oct · 9:00 am', stage: 'ready', stageDetail: 'Coming in 3 Oct' },
  { id: 'emily-brandt', name: 'Emily Brandt', age: 39, since: '2016', signals: [{ label: 'Opened 5+ emails', date: '22 Sep' }], orthoBenefit: '$2,000', lastVisit: '22 Sep', nextAppt: '—', stage: 'ready', stageDetail: 'On the Call list' },

  // Consult booked — ELVA has stopped emailing.
  { id: 'julian-office', name: 'Julian Ofori', age: 28, since: '2021', signals: [{ label: 'Booked a consult', date: '20 Sep' }], orthoBenefit: '$2,000', lastVisit: '20 Sep', nextAppt: '14 Oct · 2:00 pm', stage: 'consult-booked', stageDetail: 'Consult 14 Oct' },
  { id: 'priya-nair', name: 'Priya Nair', age: 35, since: '2017', signals: [{ label: 'Booked a consult', date: '18 Sep' }], orthoBenefit: '$1,500', lastVisit: '18 Sep', nextAppt: '9 Oct · 11:00 am', stage: 'consult-booked', stageDetail: 'Consult 9 Oct' },

  // Asked — not now — said not now; asked again after a rest.
  { id: 'todd-lane', name: 'Todd Lane', age: 44, since: '2013', signals: [{ label: 'Said not right now', date: '12 Jun' }], orthoBenefit: '$1,000', lastVisit: '12 Jun', nextAppt: '—', stage: 'asked-not-now', stageDetail: 'Ask again after 12 Dec' },
  { id: 'sofia-mendez', name: 'Sofia Mendez', age: 30, since: '2019', signals: [{ label: 'Said not right now', date: '2 Aug' }], orthoBenefit: '$2,000', lastVisit: '2 Aug', nextAppt: '—', stage: 'asked-not-now', stageDetail: 'Ask again after 2 Feb' },
];

/** The three Prospects tiles (amendment 34 §3.1). */
export const INV_PROSPECT_TILES = { asked: 38, comingIn: 9, benefitValue: '$31,500' };

/* ---- §3.3 the call list (7 ready → 5 to call + 2 coming in) --------------- */

export const INV_CALL_GROUPS: InvCallGroup[] = [
  {
    id: 'replied', title: 'Replied to an email',
    rows: [
      { id: 'leila-moradi', name: 'Leila Moradi', whatDid: 'Replied 28 Sep 8:14 pm', smileProfile: 'Gap teeth', topConcern: 'Insurance', benefit: '$1,500' },
      { id: 'grace-whitfield', name: 'Grace Whitfield', whatDid: 'Replied 26 Sep', smileProfile: 'Crooked teeth', topConcern: 'Monthly payments', benefit: '$2,000' },
    ],
  },
  {
    id: 'clicked', title: 'Clicked the consult link and didn’t book',
    rows: [
      { id: 'daniel-ortiz', name: 'Daniel Ortiz', whatDid: '29 Sep 12:40 pm, from the day-24 email — stopped at choosing a time', smileProfile: 'Overbite', topConcern: 'How long it takes', benefit: '$1,000' },
    ],
  },
  {
    id: 'asked-line', title: 'Asked the Invis-Line about cost, insurance or timing',
    rows: [
      { id: 'hannah-cole', name: 'Hannah Cole', whatDid: 'Texted 25 Sep', smileProfile: 'Crooked teeth', topConcern: 'How long it takes', benefit: '$1,500', chips: ['Graduation · May 2027', 'A parent decides'] },
    ],
  },
  {
    id: 'opened', title: 'Opened 5 or more emails',
    rows: [
      { id: 'marcus-reid', name: 'Marcus Reid', whatDid: 'Opened 7 of 7, no reply, no click', smileProfile: 'Gap teeth', topConcern: '—', benefit: '$2,000' },
    ],
  },
  {
    id: 'coming-in', title: 'Coming in within 14 days', comingIn: true, note: 'talk at the visit — the appointment shows the tips',
    rows: [
      { id: 'orville-allison', name: 'Orville Allison', whatDid: 'In today at 5:00 pm (filling, Dr. Carter)', smileProfile: 'Crooked teeth', topConcern: 'How it looks', benefit: '$1,500' },
      { id: 'sofia-delgado', name: 'Sofia Delgado', whatDid: 'In on 8 Oct at 10:00 am', smileProfile: 'Gap teeth', topConcern: 'Cost', benefit: '$2,000' },
    ],
  },
];

/* ---- §3.4 Invis-Line ----------------------------------------------------- */

export const INV_LINE_QUESTIONS: InvLineQuestion[] = [
  { label: 'Cost after insurance', count: 14 },
  { label: 'How long it takes', count: 9 },
  { label: 'Discomfort', count: 6 },
  { label: 'Eating and cleaning', count: 5 },
  { label: 'An event coming up', count: 4 },
  { label: 'Something else', count: 3 },
];

export const INV_LINE_UNANSWERED: InvLineUnanswered[] = [
  { question: 'Can I whiten my teeth while I’m wearing aligners?', patients: '2 patients' },
  { question: 'Can I switch from braces I got somewhere else?', patients: '1 patient' },
  { question: 'Do you do aligners for kids under 12?', patients: '1 patient' },
];

export const INV_LINE_PASSED: InvLinePassed[] = [
  { text: 'Called about a broken tooth — transferred to the front desk', date: '22 Sep' },
  { text: 'Asked about a bill — transferred to the billing coordinator', date: '17 Sep' },
];

export const INV_LINE_CONVERSATIONS: InvLineConversation[] = [
  { id: 'orville-allison', name: 'Orville Allison', channel: 'text', when: '24 Sep 9:12 pm', stage: 'ready', q: 'What would I actually pay with my insurance?', a: 'Good question, Orville. Your plan has an orthodontic benefit you haven’t used. What you’d pay depends on your case — a consult with Dr. Carter gives you the exact number. Would you like a time?', programmeText: { day: 21, body: 'Hi Orville — quick question: what’s the one thing holding you back on aligners? Reply and I’ll get you a straight answer.' } },
  { id: 'hannah-cole', name: 'Hannah Cole', channel: 'text', when: '25 Sep 4:02 pm', stage: 'ready', q: 'Could I be done before graduation in May?', a: 'It depends on your teeth — Dr. Carter can tell you how long yours would take at a consult. You can pick a time here: alamoheights.elva.ai/book/aligners' },
  { id: 'emily-brandt', name: 'Emily Brandt', channel: 'call', when: '27 Sep 12:15 pm', stage: 'worth-asking', q: 'Do aligners hurt?', a: 'Most people feel pressure for a day or two with each new set, then it fades. Would you like to talk to Dr. Carter about it at a consult?' },
];

/* ---- §3.1 latest --------------------------------------------------------- */

export const INV_LATEST: InvLatestItem[] = [
  { body: 'Orville Allison clicked the consult link — didn’t book. Now Ready, in Calls.', at: '27 Sep' },
  { body: 'Nadia Karimi said yes at the chair (Dana) — smile profile set, now on day 31 of the programme.', at: '30 Aug' },
  { body: 'Hannah Cole asked the Invis-Line about timing before graduation — now Ready.', at: '25 Sep' },
  { body: 'Grace Whitfield replied to the day-11 email about monthly payments.', at: '26 Sep' },
  { body: 'Marcus Reid opened five emails, no reply — nudge at his next visit.', at: '24 Sep' },
];

export const invAgentHeader = () =>
  `${String(INV_NUMBERS.ready)} ready for a nudge · ${invHoldStore.onHold ? 'on hold' : 'working'}`;

/* ---- §7 settings signals ------------------------------------------------- */

export const INV_INTEREST_SIGNALS: InvSettingsSignal[] = [
  { id: 'chart-note', name: 'Interest noted in the chart', desc: 'A provider or hygienist wrote it — aligners, straightening, asked about Invisalign', source: 'PMS notes', count: 9, on: true },
  { id: 'asked-elva', name: 'Asked ELVA about aligners', desc: 'On a call, text or email to the practice', source: 'ELVA', count: 4, on: true },
  { id: 'contacted-line', name: 'Contacted the Invis-Line', desc: 'Texted or called with a question', source: 'Invis-Line', count: 3, on: true },
  { id: 'unused-benefit', name: 'Unused orthodontic benefit', desc: 'Lifetime ortho benefit, none used', source: 'Eligibility', count: 19, on: true },
  { id: 'family', name: 'A family member in aligner or ortho treatment', desc: '', source: 'PMS family links', count: 5, on: true },
  { id: 'scan', name: 'Had a scan or consult and didn’t start', desc: 'Scan or ortho records, no case started', source: 'PMS history', count: 2, on: true },
  { id: 'cosmetic', name: 'Asked about cosmetic work', desc: 'Whitening, veneers, bonding', source: 'PMS notes + ELVA', count: 0, on: false, wouldBeAdded: 11 },
  { id: 'crowding', name: 'Crowding or bite noted by the dentist', desc: '', source: 'PMS notes', count: 0, on: false, wouldBeAdded: 27 },
];

export const INV_READY_SIGNALS: InvSettingsSignal[] = [
  { id: 'replied', name: 'Replied to an email', desc: '', source: 'Email', count: 2, on: true },
  { id: 'clicked', name: 'Clicked the consult link and didn’t book', desc: '', source: 'Booking', count: 1, on: true },
  { id: 'asked-line', name: 'Asked the Invis-Line about cost, insurance or timing', desc: '', source: 'Invis-Line', count: 2, on: true },
  { id: 'opened', name: 'Opened 5 or more emails', desc: '', source: 'Email', count: 3, on: true },
  { id: 'appt-14', name: 'Has an appointment in the next 14 days', desc: 'Talk at the visit — the appointment shows the tips', source: 'Calendar', count: 2, on: true },
  { id: 'finished', name: 'Finished the ten emails without booking', desc: '', source: 'Email', count: 0, on: false, wouldBeAdded: 4 },
];

export const INV_ALWAYS: string[] = [
  'A person records the Yes before any email goes',
  'Texts only from the Invis-Line, only with the patient’s written yes, at most 3 — never from the practice number, never an amount of money',
  'No calls — ELVA never calls about aligners; your team does, from Calls',
  'Never a case fee, “good candidate”, or a promise about results or timing',
  'One patient in one stage, and on the Call list only when a Ready signal fires',
  'Stops the moment a consult is booked',
];

/* ---- §5 the Log-the-conversation options --------------------------------- */

export const INV_LOG_BEFORE_SAY: InvLogOption[] = [
  { key: 'yes', label: 'Yes — send me information', effect: 'ELVA’s emails start today. Needs a smile profile.' },
  { key: 'not-now', label: 'Not right now', effect: 'Emails and texts stop. Asked again in 6 months.' },
  { key: 'no', label: 'No', effect: 'Closed. Nothing about aligners.', red: true },
  { key: 'didnt', label: 'Didn’t come up', effect: 'Stays Worth asking.' },
];

export const INV_LOG_AFTER_HAPPENED: InvLogOption[] = [
  { key: 'booked', label: 'Booked a consult', effect: 'ELVA stops emailing. The consult is on the calendar.' },
  { key: 'think', label: 'Wants to think about it', effect: 'Back on the list in 14 days if nothing happens.' },
  { key: 'callback', label: 'Call back on…', effect: 'Everything waits until that date, then resumes.' },
  { key: 'not-now', label: 'Not right now', effect: 'Emails and texts pause. Asked again in 6 months.' },
  { key: 'not-interested', label: 'Not interested', effect: 'Closed. Nothing more about aligners.', red: true },
  { key: 'didnt', label: 'Didn’t come up', effect: 'Nothing changes.' },
];

// The Smile-Quiz order (spec 45 §1.1): overbite … mixed baby teeth. Crowding →
// Crooked teeth, Gaps → Gap teeth.
export const INV_LOG_SMILE: string[] = ['Overbite', 'Underbite', 'Crossbite', 'Gap teeth', 'Open bite', 'Crooked teeth', 'Generally straight', 'Mixed baby teeth'];
export const INV_LOG_CONCERNS: string[] = ['Cost', 'Insurance', 'Monthly payments', 'Discomfort', 'How long it takes', 'How it looks', 'Eating and cleaning', 'Tried braces before', 'Worried about results'];
export const INV_LOG_EVENTS: string[] = ['Wedding', 'Graduation', 'Photos or a big event', 'Other'];

/** The fixed Don't line (§4 / §6). */
export const INV_DONT = 'Don’t quote a case fee · don’t say “good candidate” · don’t promise results or a finish date';

/* ---- §6 the patient tab (p001 Orville Allison) --------------------------- */

export const INV_PATIENT: InvPatient = {
  id: 'orville-allison',
  name: 'Orville Allison',
  first: 'Orville',
  last: 'Allison',
  initials: 'OA',
  stage: 'ready',
  headerLine: '34 · patient since 2018 · 11 visits · (210) 555-0148 · Delta Dental PPO · in today at 5:00 pm (filling, Dr. Carter)',
  orthoBenefit: '$1,500',
  orthoBenefitNote: 'never used',
  readySince: 'Ready since 24 Sep',
  readyWhy: 'asked the Invis-Line what he’d pay, then clicked the consult link on 27 Sep and didn’t book.',
  nextStepNote: 'Talk to him at today’s 5:00 pm visit.',
  whatToSay: [
    { label: 'Open with', body: 'His question on the Invis-Line: what he’d actually pay with his insurance.' },
    { label: 'The money', body: 'He has $1,500 of orthodontic benefit he’s never used (Delta Dental, checked 22 Sep). A consult gives the exact number.' },
    { label: 'His worry', body: 'How it looks at work — the trays are clear, most people don’t notice them.' },
    { label: 'Timing', body: 'Getting married in June 2027. A consult tells him how long his would take.' },
  ],
  smileProfile: ['Crooked teeth'],
  concerns: ['Cost', 'How it looks'],
  whoDecides: 'Partner',
  comingUpEvent: 'Wedding',
  comingUpMonth: 'June 2027',
  bestTime: 'Evening',
  whyProspect: [
    { label: 'Interest noted in the chart — Dana (hygienist)', date: '30 Aug' },
    { label: 'Unused ortho benefit — $1,500 lifetime, none used · Delta Dental PPO', date: 'checked 22 Sep' },
  ],
  askBy: 'Dana asked at the chair on 30 Aug',
  askAnswer: 'Yes, send me information',
  emails: {
    day: 24, of: 58, sent: 6, textsSent: 2, opened: 5, clickedNote: 'consult link clicked 27 Sep',
    nextDay: 31, nextSubject: 'What the first appointment involves',
    nextHeldNote: 'Held until 1 Oct — goes then if nobody logs a conversation.',
  },
  lineConvos: [
    { at: 'Text · 24 Sep 9:12 pm', q: 'What would I actually pay with my insurance?', a: 'Your plan has an orthodontic benefit you haven’t used. What you’d pay depends on your case — a consult with Dr. Carter gives you the exact number. Would you like a time?', reply: 'Maybe. I’m in on the 30th anyway.' },
  ],
  history: [
    { at: '27 Sep', kind: 'Clicked the consult link · didn’t book' },
    { at: '24 Sep', kind: 'Texted the Invis-Line', toStage: 'ready' },
    { at: '23 Sep', kind: 'Day-24 email · opened' },
    { at: '30 Aug', kind: 'Dana logged the conversation', chips: ['Yes — send me information', 'Crooked teeth', 'How it looks'] },
  ],
  textsConsent: { status: 'yes', detail: '31 Aug · he tapped Yes, text me too in email 1 · 2 of 3 sent' },
};

/* ---- The programme (amendment 33 §1) ------------------------------------- */

/** The fields the email editor can insert. */
export const INV_PROGRAMME_FIELDS = ['First name', 'Smile profile', 'Ortho benefit left', 'Top concern', 'Provider level', 'Consult booking link', 'Practice name', 'Doctor'];

/** The 13 programme messages — 10 emails + 3 texts, in day order. */
// ELVA's versions of the 13 messages (spec 35). The English is what "Go back to
// ELVA's version" restores; es/fa are short translation stubs (read-only in the
// editor). Each email ends with {Consult booking link} so the Save-gate holds.
export const INV_PROGRAMME: [InvProgrammeMessage, ...InvProgrammeMessage[]] = [
  {
    id: 'email-1', day: 0, kind: 'email', title: 'What clear aligners actually are', subject: 'What clear aligners actually are',
    body: {
      en: 'Hi {First name},\n\nThanks for saying yes at your visit. Here’s the short version of what clear aligners are.\n\nThey’re a series of thin, clear trays made for your teeth. Each set moves your teeth a small step, and you switch to the next set every week or two. Most people around you won’t notice you’re wearing them, and you take them out to eat.\n\nOver the next couple of months we’ll send you a few short emails — what it costs, how it feels, how it compares to braces. You can stop them any time with the link at the bottom, and it won’t stop anything else from us.\n\n{Provider level}\n\nIf you’d like, we can also send you a few texts — three at most, over two months, from our aligner line.\n\n{Consult booking link}\n\nQuestions any time? Text or call our aligner line: (210) 555-0199.\n\n— The team at {Practice name}',
      es: 'Hola {First name}, gracias por decir que sí. Los alineadores transparentes son fundas finas y casi invisibles que enderezan los dientes poco a poco.',
      fa: 'سلام {First name}، ممنون که موافقت کردید. الاینرهای شفاف قالب‌های نازک و کم‌دیده هستند که دندان‌ها را کم‌کم مرتب می‌کنند.',
    },
    fields: ['First name', 'Provider level', 'Consult booking link', 'Practice name'], sent: 24, opened: 19,
  },
  {
    id: 'text-1', day: 1, kind: 'text', title: 'Welcome to the aligner line',
    body: {
      en: 'Hi {First name}, it’s {Practice name}’s aligner line. Questions about clear aligners — cost, comfort, anything? Just text us here.',
      es: 'Hola {First name}, es la línea de alineadores de {Practice name}. ¿Dudas sobre coste o comodidad? Escríbenos.',
      fa: 'سلام {First name}، این خط الاینر {Practice name} است. هر سوالی دارید همین‌جا پیام دهید.',
    },
    fields: ['First name', 'Practice name'], sent: 11, replied: 4,
  },
  {
    id: 'email-5', day: 5, kind: 'email', title: 'Why people with {Smile profile} use them', subject: 'Why people with {Smile profile} choose clear aligners',
    body: {
      en: 'Hi {First name},\n\n{Smile profile line}\n\nIt’s one of the most common reasons people come to us about clear aligners — and it’s usually the first thing they notice changing.\n\nWhat happens in your case is something only {Doctor} can tell you, after looking at your teeth and bite. That’s what a consult is for: no commitment, just a clear picture and a plan you can think about.\n\n{Consult booking link}\n\nQuestions? Text our aligner line: (210) 555-0199.\n\n— The team at {Practice name}',
      es: 'Hola {First name}, es una de las razones más comunes por las que la gente pregunta por los alineadores.',
      fa: 'سلام {First name}، این یکی از رایج‌ترین دلایلی است که افراد سراغ الاینر می‌آیند.',
    },
    fields: ['First name', 'Smile profile', 'Doctor', 'Consult booking link', 'Practice name'], sent: 23, opened: 16,
  },
  {
    id: 'email-11', day: 11, kind: 'email', title: 'What it costs, and what your plan covers', subject: 'What it costs, and what your plan covers',
    body: {
      en: 'Hi {First name},\n\nMost people’s first question about clear aligners is the cost, so here’s a straight answer.\n\nThe price depends on your teeth — how much they need to move, and for how long. {Doctor} works that out at a consult, and you leave with an exact number, not an estimate.\n\n{Ortho benefit left} Orthodontic benefit is usually a lifetime amount, so it doesn’t reset in January.\n\nWe also offer monthly payments, so you don’t pay it all at once.\n\n{Consult booking link}\n\nQuestions? Text our aligner line: (210) 555-0199.\n\n— The team at {Practice name}',
      es: 'Hola {First name}, la mayoría pregunta primero por el coste. Depende de tus dientes; una consulta te da la cifra exacta.',
      fa: 'سلام {First name}، اولین سوال بیشتر افراد هزینه است. بسته به دندان‌های شماست؛ مشاوره رقم دقیق را می‌دهد.',
    },
    fields: ['First name', 'Ortho benefit left', 'Doctor', 'Consult booking link', 'Practice name'], sent: 22, opened: 17,
  },
  {
    id: 'email-17', day: 17, kind: 'email', title: 'Aligners or braces — an honest comparison', subject: 'Aligners or braces? An honest comparison',
    body: {
      en: 'Hi {First name},\n\nBoth clear aligners and braces straighten teeth. They just go about it differently.\n\nClear aligners: you can take them out to eat and brush, and they’re hard to spot. They only work if you wear them most of the day — most of the work is yours.\n\nBraces: fixed in place, so they work without you thinking about it, and some bigger moves are easier with them.\n\nFor some people braces really are the better choice, and we’ll tell you if that’s you. Either way, {Doctor} will explain what would work for your teeth and why.\n\n{Consult booking link}\n\nQuestions? Text our aligner line: (210) 555-0199.\n\n— The team at {Practice name}',
      es: 'Hola {First name}, los alineadores y los brackets enderezan los dientes de formas distintas. {Doctor} te dirá qué conviene.',
      fa: 'سلام {First name}، الاینر و براکت هر دو دندان را مرتب می‌کنند اما به روش متفاوت. {Doctor} بهترین گزینه را می‌گوید.',
    },
    fields: ['First name', 'Doctor', 'Consult booking link', 'Practice name'], sent: 20, opened: 12,
  },
  {
    id: 'text-21', day: 21, kind: 'text', title: 'A quick question check-in',
    body: {
      en: 'Hi {First name}, quick one from {Practice name}: what’s the one thing you’d want to know about aligners? Reply and we’ll answer.',
      es: 'Hola {First name}, una rápida de {Practice name}: ¿qué querrías saber sobre los alineadores? Responde y te contamos.',
      fa: 'سلام {First name}، یک سوال از {Practice name}: چه چیزی درباره الاینر می‌خواهید بدانید؟ پاسخ دهید.',
    },
    fields: ['First name', 'Practice name'], sent: 8, replied: 3,
  },
  {
    id: 'email-24', day: 24, kind: 'email', title: 'How long people usually wear them', subject: 'How long would clear aligners take?',
    body: {
      en: 'Hi {First name},\n\nIt’s the question we hear most after cost: how long?\n\nIt varies a lot from person to person. Small changes can be quick; bigger moves take longer. What decides it is how far your teeth need to move — and how many hours a day the trays are in.\n\nThe only honest answer for you comes from {Doctor} at a consult, where you’ll see the plan step by step.\n\n{Top concern sentence}\n\n{Consult booking link}\n\nQuestions? Text our aligner line: (210) 555-0199.\n\n— The team at {Practice name}',
      es: 'Hola {First name}, la segunda pregunta más común es cuánto tarda. Depende de cada persona; una consulta te lo muestra paso a paso.',
      fa: 'سلام {First name}، دومین سوال رایج مدت زمان است. برای هر فرد فرق دارد؛ مشاوره آن را مرحله‌به‌مرحله نشان می‌دهد.',
    },
    fields: ['First name', 'Top concern', 'Doctor', 'Consult booking link', 'Practice name'], sent: 18, opened: 11,
  },
  {
    id: 'email-31', day: 31, kind: 'email', title: 'What the first appointment involves', subject: 'What happens at an aligner consult',
    body: {
      en: 'Hi {First name},\n\nIf you’re wondering what a consult actually involves, here it is.\n\n{Doctor} looks at your teeth and bite, takes a few photos and usually a quick scan of your teeth — no needles, and nothing that hurts. Then you talk it through: what would change, what it would take, and what it costs.\n\nAnd once you start: most people feel some pressure for a day or two with each new set of trays. That’s the trays doing their job, and it fades.\n\nYou leave with a plan and no obligation to start.\n\n{Consult booking link}\n\nQuestions? Text our aligner line: (210) 555-0199.\n\n— The team at {Practice name}',
      es: 'Hola {First name}, en la consulta {Doctor} revisa tus dientes, toma fotos y un escaneo rápido — sin agujas ni dolor.',
      fa: 'سلام {First name}، در مشاوره {Doctor} دندان‌ها را می‌بیند، چند عکس و یک اسکن سریع می‌گیرد — بدون سوزن و بدون درد.',
    },
    fields: ['First name', 'Doctor', 'Consult booking link', 'Practice name'], sent: 15, opened: 10,
  },
  {
    id: 'email-38', day: 38, kind: 'email', title: 'Eating, cleaning, and the bits people ask about', subject: 'Eating, cleaning, and the everyday questions',
    body: {
      en: 'Hi {First name},\n\nThe practical questions, answered before you have to ask:\n\n• Eating: take the trays out, eat anything you like, brush, and put them back.\n• Drinking: water is fine with them in; take them out for anything else.\n• Cleaning: rinse and brush the trays gently each day.\n• Wearing them: most of the day and night — they work while they’re in.\n• Talking: a slight lisp for a day or two, then it goes.\n\nIf you’ve got a question that isn’t here, text it to us.\n\n{Consult booking link}\n\nQuestions? Text our aligner line: (210) 555-0199.\n\n— The team at {Practice name}',
      es: 'Hola {First name}, lo práctico: comes sin las fundas, bebes agua con ellas puestas, y las limpias cada día.',
      fa: 'سلام {First name}، نکات روزمره: برای غذا درشان می‌آورید، با آب می‌توانید نگهشان دارید، و هر روز تمیزشان می‌کنید.',
    },
    fields: ['First name', 'Consult booking link', 'Practice name'], sent: 12, opened: 8,
  },
  {
    id: 'text-45', day: 45, kind: 'text', title: 'Pick a consult time',
    body: {
      en: 'Hi {First name}, whenever you’re ready, you can pick a consult time with {Doctor} here: {Consult booking link}',
      es: 'Hola {First name}, cuando quieras, elige una hora con {Doctor} aquí: {Consult booking link}',
      fa: 'سلام {First name}، هر وقت آماده بودید، زمان مشاوره با {Doctor} را اینجا انتخاب کنید: {Consult booking link}',
    },
    fields: ['First name', 'Doctor', 'Consult booking link'], sent: 3, replied: 1,
  },
  {
    id: 'email-45', day: 45, kind: 'email', title: 'A question we get a lot', subject: 'A question we get a lot: will people notice?',
    body: {
      en: 'Hi {First name},\n\nOne question comes up more than almost any other: will people notice?\n\nMostly, no. The trays are clear and thin, and most people won’t know unless you tell them. You’ll take them out to eat, so they’re never on show at a meal.\n\nGot a different question? Reply to this email or text us — we answer every one.\n\n{Consult booking link}\n\nQuestions? Text our aligner line: (210) 555-0199.\n\n— The team at {Practice name}',
      es: 'Hola {First name}, una pregunta muy común: ¿se nota? Casi nunca — las fundas son transparentes y finas.',
      fa: 'سلام {First name}، یک سوال رایج: دیده می‌شود؟ تقریباً نه — قالب‌ها شفاف و نازک‌اند.',
    },
    fields: ['First name', 'Consult booking link', 'Practice name'], sent: 9, opened: 7,
  },
  {
    id: 'email-52', day: 52, kind: 'email', title: 'What happens when you finish', subject: 'What happens when you finish',
    body: {
      en: 'Hi {First name},\n\nHere’s the part people most often say they wish they’d been told at the start.\n\nWhen the last set of trays is done, you wear a retainer to keep your teeth where they are — usually every night at first, then a few nights a week. Teeth naturally drift over time, so a retainer is for as long as you want to keep the result.\n\n{Doctor} will go through it at your consult, along with everything else.\n\n{Consult booking link}\n\nQuestions? Text our aligner line: (210) 555-0199.\n\n— The team at {Practice name}',
      es: 'Hola {First name}, al terminar usas un retenedor para mantener el resultado. {Doctor} te lo explica en la consulta.',
      fa: 'سلام {First name}، در پایان یک نگه‌دارنده می‌پوشید تا نتیجه حفظ شود. {Doctor} در مشاوره توضیح می‌دهد.',
    },
    fields: ['First name', 'Doctor', 'Consult booking link', 'Practice name'], sent: 6, opened: 3,
  },
  {
    id: 'email-58', day: 58, kind: 'email', title: 'If you ever want to talk it through', subject: 'Whenever you’re ready',
    body: {
      en: 'Hi {First name},\n\nThis is the last email in this series — we won’t keep filling your inbox.\n\nIf clear aligners are still on your mind, the next step is simple: a consult with {Doctor}, where you get a clear picture, an exact price and time to think about it.\n\n{Ortho benefit left}\n\n{Consult booking link}\n\nOr text our aligner line any time: (210) 555-0199. We’re happy to help, whenever that is.\n\n— The team at {Practice name}',
      es: 'Hola {First name}, este es el último correo. Si los alineadores siguen en tu mente, una consulta con {Doctor} es el siguiente paso.',
      fa: 'سلام {First name}، این آخرین ایمیل است. اگر هنوز به الاینر فکر می‌کنید، گام بعدی یک مشاوره با {Doctor} است.',
    },
    fields: ['First name', 'Ortho benefit left', 'Doctor', 'Consult booking link', 'Practice name'], sent: 3, opened: 2,
  },
];

/** The six fixed Invis-Line replies (§1). Four editable, two locked. */
export const INV_LINE_REPLIES: InvLineReply[] = [
  { id: 'first', label: 'First reply to a new question', body: 'Thanks for reaching out to the {Practice name} aligner line — happy to help. What would you like to know?' },
  { id: 'ask-text', label: 'Asking to text, in a thread they started', body: 'Would it be OK to send you a few texts about clear aligners — three at most, over two months? Reply YES and I’ll add you.' },
  { id: 'cant-answer', label: 'When ELVA can’t answer', body: 'Good question — let me get a person from the team to follow up with you on that.' },
  { id: 'not-aligners', label: 'Not about aligners', body: 'Happy to help with that — I’ll pass this to the front desk so they can take care of it.' },
  { id: 'stop', label: 'STOP', body: 'You’re unsubscribed from aligner texts. You won’t get any more. Reply START to turn them back on.', locked: true },
  { id: 'help', label: 'HELP', body: '{Practice name} · (210) 555-0199. Standard message and data rates may apply.', locked: true },
];

/** The consent-page copy (§2, INV11TextConsent). */
export const INV_TEXT_CONSENT = {
  title: 'Texts about clear aligners',
  fromNumber: '(210) 555-0199',
  patientMobile: '••• ••• ••48',
  checkbox: 'Yes — send me up to three texts about clear aligners over the next two months, from the Alamo Heights aligner line. I can reply STOP anytime.',
  done: 'You’re all set',
};

/** Overview sub-line and the said-yes counts (§3). */
export const INV_PROGRAMME_STATS = { saidYes: 11, total: 21 };
